import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue } from 'vue'
import { useAuthStore } from '~~/stores/authStore'
import { extractApiErrorMessage } from '../../utils/apiErrorMessage'
import { useNotifications } from '../useNotifications'
import { useCatalogOnly } from '../useCatalogOnly'
import type { MaybeRefOrGetter, Ref } from 'vue'
import type {
  Address,
  AffiliateStats,
  BluepaymentGatewayList,
  BluepaymentPaymentResult,
  BluepaymentPollingResult,
  CartLine,
  CartRecoverResult,
  Category,
  CategorySubItem,
  CheckoutPrepareResult,
  CheckoutValidationResult,
  CmsPage,
  CmsPageSummary,
  CodOrderResult,
  DeliveryCountryNotice,
  InpostCartChoice,
  InpostGeowidgetToken,
  InpostIntlGeowidgetConfig,
  OrlenGeowidgetToken,
  ManufacturerListItem,
  NavItem,
  Order,
  OrderCreateResult,
  PaginationData,
  PaymentMethod,
  PickupPoint,
  Popup,
  Product,
  ReturnRequest,
  SetCheckoutCarrierResult,
  ShippingMethod,
  ShopReviewsData,
  StoreBanner,
  StoreBannersData,
  StoreCartCouponsData,
  StoreHomeContent,
  StoreListingResult,
  StorePromoCoupon,
} from '~~/shared/types/store'
import type { RestLooseObject } from '~~/shared/types/rest'
import { useServerQuery } from './useServerQuery'
import { normalizeListingQueryState } from './listingQueryState'
import { resolveCartQueryResult } from '../cart/resolveCartQueryResult'
import { reportCartSessionLost } from '../cart/reportCartSessionLost'
import { resetGuestCartCache } from '../cart/guestCartState'
import { cartIdentityHeaders, setCartIdentityToken } from '../cart/cartIdentityToken'

interface HomeQueryData {
  hero: {
    eyebrow: string
    title: string
    text: string
    ctaLabel: string
    ctaHref: string
    image: string
  }
  featured: Product[]
  trending: Product[]
  favorites: Product[]
  categories: Category[]
}

interface CategoryQueryData {
  category: Category
  listing?: StoreListingResult
  products: Product[]
  pagination: PaginationData
}

interface CategoryFiltersQueryData {
  category: Category
  listing: StoreListingResult
}

interface ProductQueryData {
  product: Product
  related: Product[]
  othersAlsoViewed: Product[]
  bestInCategory: Product[]
}

interface ProductRecommendationsData {
  othersAlsoViewed: Product[]
  bestInCategory: Product[]
  isFallback?: boolean
}

interface CartRecommendationsData {
  products: Product[]
}

export interface SearchQueryData extends StoreListingResult {
  query: string
  topCategories: string[]
}

interface SearchFiltersQueryData {
  listing: StoreListingResult
}

interface NewProductsQueryData {
  listing?: StoreListingResult
  products: Product[]
}

interface NewProductsFiltersQueryData {
  listing: StoreListingResult
}

interface CmsPagesQueryData {
  pages: CmsPageSummary[]
}

interface CmsPageQueryData {
  page: CmsPage
}

interface StoreQueryOptions {
  enabled?: MaybeRefOrGetter<boolean>
  ssr?: MaybeRefOrGetter<boolean>
  keepPreviousData?: boolean
}

interface CartQueryData {
  cartId: number
  // Token tożsamości koszyka — patrz cartIdentityToken. Utrwalany przy każdej
  // odpowiedzi i odsyłany nagłówkiem X-Cart-Id, by koszyk przeżył utratę cookie.
  cartToken?: string
  lines: CartLine[]
  // Pozycje wyprzedane — osobno od `lines`, bo nie da się ich kupić: nie liczą się
  // do sum ani licznika sztuk, ale zostają widoczne w sekcji „Wyprzedane".
  unavailableLines?: CartLine[]
  subtotal: number
  shippingCost: number
  discountValue: number
  total: number
  couponCode: string
  itemsCount: number
  errors: string[]
  // Ustawiane przez /api/store/cart tylko na ścieżce 422 (utracona sesja PS),
  // NIE dla realnie pustego koszyka. Patrz resolveCartQueryResult.
  sessionLost?: boolean
}

interface WishlistQueryData {
  productIds?: string[]
  products?: Product[]
}

export interface GuestSessionContract {
  id_customer?: number | string
  cart?: {
    id?: number | string
  } | null
  cart_id?: number | string
}

export type SocialAuthProvider = 'google' | 'facebook'

export interface SocialPendingAuth {
  provider: SocialAuthProvider
  email: string
  firstName: string
  lastName: string
  expiresAt: string
}

interface AccountInfoData {
  id?: number
  email?: string
  firstname?: string
  lastname?: string
  id_gender?: number
  birthday?: string | null
  newsletter?: boolean
  is_guest?: boolean
  active?: boolean
  additional_data?: RestLooseObject | null
}

export interface CountryOption {
  id: number
  name: string
  isoCode: string
  needZipCode?: boolean
  zipCodeFormat?: string | null
}

export type { CheckoutValidationResult, OrderCreateResult }

export interface AccountUpdatePayload {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  new_password?: string
  gender?: number
  newsletter?: boolean
  birthday?: string
}

export interface AddressPayload {
  alias: string
  firstname: string
  lastname: string
  id_country: number
  country: string
  postcode: string
  city: string
  address1: string
  address2?: string | null
  company?: string | null
  other?: string | null
  phone?: string | null
  phone_mobile?: string | null
  vat_number?: string | null
  dni?: string | null
  id_state?: number
}

interface CheckoutValidatePayload {
  id_address?: number
  id_address_delivery?: number
  id_address_invoice?: number
  id_carrier: number
  coupon_code?: string
  payment_method?: string
}

interface OrderAddressPayload {
  country_code: string
  zip: string
  city: string
  address1: string
  address2: string | null
  first_name: string
  last_name: string
  phone: string | null
  phone_mobile: string | null
  province: string | null
  company: string | null
  vat_number: string | null
}

interface OrderCustomerPayload {
  email: string
  first_name: string
  last_name: string
}

interface OrderLinePayload {
  product_id: number
  quantity: number
}

interface CreateOrderPayload {
  authenticated: boolean
  customer: OrderCustomerPayload
  billing_address: OrderAddressPayload
  shipping_address: OrderAddressPayload
  shipping_lines: {
    carrier_id: number
  }
  line_items: OrderLinePayload[]
  payment_method_id: string
  newsletter: boolean
}

interface CheckoutPreparePayload {
  customer: OrderCustomerPayload
  billing_address: OrderAddressPayload
  shipping_address: OrderAddressPayload
  shipping_lines: {
    carrier_id: number
  }
}

function key(...parts: string[]) {
  return parts
}

const storeKeys = {
  nav: key('store', 'nav'),
  home: key('store', 'home'),
  homeContent: key('store', 'home-content'),
  category: (slug: string, page: number) => key('store', 'category', slug, String(page)),
  categoryById: (id: string, page: number) => key('store', 'category-by-id', id, String(page)),
  manufacturers: key('store', 'manufacturers'),
  manufacturer: (id: string) => key('store', 'manufacturer', id),
  product: (slug: string) => key('store', 'product', slug),
  search: (query: string) => key('store', 'search', query),
  cart: key('store', 'cart'),
  cartCoupons: key('store', 'cart', 'coupons'),
  wishlist: key('store', 'wishlist'),
  // Prefiks — unieważnienia (`invalidateQueries`) łapią nim listy dla KAŻDEGO kraju.
  shippingMethods: key('store', 'checkout', 'carriers'),
  // Bez kraju — dokładnie dawny klucz (lista jak przed wysyłką zagraniczną).
  shippingMethodsFor: (country: string) => country
    ? key('store', 'checkout', 'carriers', country)
    : key('store', 'checkout', 'carriers'),
  paymentMethods: key('store', 'checkout', 'payment-options'),
  bluepaymentGateways: key('store', 'checkout', 'bluepayment-gateways'),
  orders: key('store', 'orders'),
  order: (id: string) => key('store', 'orders', id),
  returns: key('store', 'returns'),
  returnItem: (id: string) => key('store', 'returns', id),
  productRelated: (id: string) => key('store', 'products', id, 'related'),
  productReviews: (id: string) => key('store', 'products', id, 'reviews'),
  productStock: (id: string, attrId?: string) => attrId
    ? key('store', 'products', id, 'stock', attrId)
    : key('store', 'products', id, 'stock'),
  stockAlerts: key('store', 'products', 'stock-alerts'),
  categoryTree: key('store', 'category-tree'),
  newProducts: key('store', 'new-products'),
  guestSession: key('store', 'guest-session'),
  socialPending: key('store', 'auth', 'social-pending'),
  accountInfo: key('store', 'account', 'info'),
  addresses: key('store', 'account', 'addresses'),
  affiliate: key('store', 'account', 'affiliate'),
  addressForm: key('store', 'account', 'address-form'),
  cmsPages: key('store', 'cms', 'pages'),
  cmsPage: (slug: string) => key('store', 'cms', 'pages', slug),
  carrierHours: key('store', 'carrier-hours'),
  popups: key('store', 'popups'),
  banners: key('store', 'banners'),
  reviews: key('store', 'reviews'),
  inpostGeowidget: key('inpost', 'geowidget-token'),
  inpostClosestPoint: (postcode: string, carrierId: number) =>
    key('inpost', 'closest-point', postcode, String(carrierId)),
  inpostCartChoice: key('inpost', 'cart-choice'),
  orlenGeowidget: key('orlen', 'geowidget-token'),
  inpostIntlGeowidget: key('inpost-intl', 'geowidget-config'),
  deliveryCountryNotice: key('store', 'geo', 'delivery-country'),
}

function invalidateStoreQueries(queryClient: ReturnType<typeof useQueryClient>, keys: ReadonlyArray<readonly unknown[]>) {
  return Promise.all(keys.map(queryKey => queryClient.invalidateQueries({ queryKey })))
}

/** Rozpoznaje kształt koszyka, by nie zapisać do cache czegoś, co nim nie jest. */
function isCartPayload(value: unknown): value is CartQueryData {
  return !!value
    && typeof value === 'object'
    && typeof (value as CartQueryData).itemsCount === 'number'
    && Array.isArray((value as CartQueryData).lines)
}

/**
 * Domyka mutację koszyka JEJ WŁASNĄ odpowiedzią.
 *
 * POST /api/store/cart zwraca autorytatywny koszyk już PO mutacji, więc bierzemy
 * go wprost do cache i CELOWO nie inwalidujemy klucza koszyka. Wymuszony GET
 * mógłby trafić w chwilowo nieczytelną sesję PS (422 → sessionLost); wtedy
 * resolveCartQueryResult zachowuje POPRZEDNI koszyk z cache, cofając właśnie
 * wykonaną mutację do snapshotu sprzed niej — to była przyczyna zgłoszeń
 * „usunąłem produkt, a on nadal jest w koszyku" (kasowanie na serwerze się
 * udawało, UI pokazywał stan sprzed). Dopiero brak kształtu koszyka w odpowiedzi
 * (np. pusty body z proxy) uzasadnia powrót do invalidacji.
 *
 * Kupony inwalidujemy zawsze: usunięcie/zmiana ilości może unieważnić voucher,
 * bo backend przy każdej mutacji przelicza CartRule::autoRemoveFromCart/autoAddToCart.
 */
async function adoptCartMutationResult(queryClient: ReturnType<typeof useQueryClient>, data: unknown) {
  if (isCartPayload(data)) {
    setCartIdentityToken(data.cartToken)
    queryClient.setQueryData(storeKeys.cart, data)
  }
  else {
    await invalidateStoreQueries(queryClient, [storeKeys.cart])
  }

  await invalidateStoreQueries(queryClient, [storeKeys.cartCoupons])
}

export async function clearSocialAuthCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.removeQueries({ queryKey: storeKeys.socialPending })
  await invalidateStoreQueries(queryClient, [storeKeys.accountInfo, storeKeys.cart, storeKeys.wishlist, storeKeys.orders])
}

function fetchStore<T>(path: string, options?: {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: BodyInit | object | null
  query?: Record<string, unknown>
  credentials?: RequestCredentials
  headers?: Record<string, string>
}) {
  return $fetch<T>(path, {
    credentials: 'include',
    ...options,
  })
}

const availabilitySensitiveQueryOptions = {
  staleTime: 5 * 60_000,
  gcTime: 10 * 60_000,
  refetchOnWindowFocus: false,
} as const

const categoryProductsFastQueryOptions = {
  staleTime: 30_000,
  gcTime: 5 * 60_000,
} as const

const cmsQueryOptions = {
  staleTime: 60 * 60_000,
  gcTime: 6 * 60 * 60_000,
  refetchOnWindowFocus: false,
} as const

export function useNavQuery() {
  return useServerQuery(useQuery<{ items: NavItem[] }>({
    queryKey: storeKeys.nav,
    queryFn: () => fetchStore<{ items: NavItem[] }>('/api/store/nav'),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  }))
}

export function useNewProductsQuery(
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? options.enabled

  return useServerQuery(useQuery<NewProductsQueryData>({
    queryKey: computed(() => [...storeKeys.newProducts, ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<NewProductsQueryData>('/api/store/new-products', {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useNewProductsFiltersQuery(
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? false
  return useServerQuery(useQuery<NewProductsFiltersQueryData>({
    queryKey: computed(() => ['store', 'new-products-filters', ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<NewProductsFiltersQueryData>('/api/store/new-products/filters', {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useHomeQuery() {
  return useServerQuery(useQuery<HomeQueryData>({
    queryKey: storeKeys.home,
    queryFn: () => fetchStore<HomeQueryData>('/api/store/home'),
    ...availabilitySensitiveQueryOptions,
  }))
}

interface HomeContentQueryData {
  homeContent: StoreHomeContent
}

export function useHomeContentQuery() {
  return useServerQuery(useQuery<HomeContentQueryData>({
    queryKey: storeKeys.homeContent,
    queryFn: () => fetchStore<HomeContentQueryData>('/api/store/home-content'),
    staleTime: 60_000,
    gcTime: 15 * 60_000,
    refetchOnWindowFocus: false,
  }))
}

export function useCmsPagesQuery(options: StoreQueryOptions = {}) {
  return useServerQuery(useQuery<CmsPagesQueryData>({
    queryKey: storeKeys.cmsPages,
    queryFn: () => fetchStore<CmsPagesQueryData>('/api/store/cms/pages'),
    ...cmsQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.enabled)
}

export function useCmsPageQuery(
  slug: MaybeRefOrGetter<string>,
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? options.enabled

  return useServerQuery(useQuery<CmsPageQueryData>({
    queryKey: computed(() => storeKeys.cmsPage(toValue(slug))),
    queryFn: () => fetchStore<CmsPageQueryData>(`/api/store/cms/pages/${toValue(slug)}`),
    ...cmsQueryOptions,
    enabled: computed(() => Boolean(toValue(slug)) && (toValue(options.enabled) ?? true)),
  }), ssrBlocking)
}

export function useCategoryQuery(
  slug: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  return useServerQuery(useQuery<CategoryQueryData>({
    queryKey: computed(() => ['store', 'category', toValue(slug), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<CategoryQueryData>(`/api/store/categories/${toValue(slug)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.enabled)
}

export function useCategoryByIdQuery(
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr !== undefined ? options.ssr : options.enabled
  return useServerQuery(useQuery<CategoryQueryData>({
    queryKey: computed(() => ['store', 'category-by-id', toValue(id), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<CategoryQueryData>(`/api/store/categories/by-id/${toValue(id)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useCategoryFiltersQuery(
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? false
  return useServerQuery(useQuery<CategoryFiltersQueryData>({
    queryKey: computed(() => ['store', 'category-filters', toValue(id), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<CategoryFiltersQueryData>(`/api/store/categories/filters/${toValue(id)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useCategoryProductsFastQuery(
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? options.enabled

  return useServerQuery(useQuery<CategoryQueryData>({
    queryKey: computed(() => ['store', 'category-products-fast', toValue(id), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<CategoryQueryData>(`/api/store/categories/products/${toValue(id)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...categoryProductsFastQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export async function prefetchNewProductsQuery(
  queryClient: ReturnType<typeof useQueryClient>,
  query: Record<string, unknown>,
): Promise<NewProductsQueryData | undefined> {
  const queryKey = [...storeKeys.newProducts, ...normalizeListingQueryState(query as Record<string, never>)]
  await queryClient.prefetchQuery({
    queryKey,
    queryFn: () => fetchStore<NewProductsQueryData>('/api/store/new-products', { query }),
    ...availabilitySensitiveQueryOptions,
  })
  return queryClient.getQueryData<NewProductsQueryData>(queryKey)
}

export async function prefetchCategoryProductsFastQuery(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  query: Record<string, unknown>,
): Promise<CategoryQueryData | undefined> {
  const queryKey = ['store', 'category-products-fast', id, ...normalizeListingQueryState(query as Record<string, never>)]
  await queryClient.prefetchQuery({
    queryKey,
    queryFn: () => fetchStore<CategoryQueryData>(`/api/store/categories/products/${id}`, { query }),
    ...categoryProductsFastQueryOptions,
  })
  return queryClient.getQueryData<CategoryQueryData>(queryKey)
}

interface ManufacturerQueryData {
  manufacturer: {
    id: number
    name: string
    seoTitle: string
    seoDescription: string
    imageUrl: string
    shortDescription: string
  }
  listing?: StoreListingResult
  products: Product[]
}

interface ManufacturersQueryData {
  manufacturers: ManufacturerListItem[]
}

interface ManufacturerFiltersQueryData {
  manufacturer: {
    id: number
    name: string
    seoTitle: string
    seoDescription: string
    shortDescription: string
  }
  listing: StoreListingResult
}

export function useManufacturersQuery(options: StoreQueryOptions = {}) {
  return useServerQuery(useQuery<ManufacturersQueryData>({
    queryKey: storeKeys.manufacturers,
    queryFn: () => fetchStore<ManufacturersQueryData>('/api/store/manufacturers'),
    staleTime: 60 * 60_000,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.enabled)
}

export function useManufacturerQuery(
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  return useServerQuery(useQuery<ManufacturerQueryData>({
    queryKey: computed(() => [...storeKeys.manufacturer(toValue(id)), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<ManufacturerQueryData>(`/api/store/manufacturer/${toValue(id)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.enabled)
}

export function useManufacturerFiltersQuery(
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? false
  return useServerQuery(useQuery<ManufacturerFiltersQueryData>({
    queryKey: computed(() => ['store', 'manufacturer-filters', toValue(id), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<ManufacturerFiltersQueryData>(`/api/store/manufacturer/filters/${toValue(id)}`, {
      query: toValue(query) as Record<string, unknown>,
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useCategoryTreeQuery() {
  return useServerQuery(useQuery<{ categories: CategorySubItem[] }>({
    queryKey: storeKeys.categoryTree,
    queryFn: () => fetchStore<{ categories: CategorySubItem[] }>('/api/store/categories/tree'),
    staleTime: 60 * 60 * 1000,
  }))
}

interface CarrierHoursQueryData {
  text: string
  validUntil: string | null
}

export function useCarrierHoursQuery(options: StoreQueryOptions = {}) {
  return useServerQuery(useQuery<CarrierHoursQueryData>({
    queryKey: storeKeys.carrierHours,
    queryFn: () => fetchStore<CarrierHoursQueryData>('/api/store/carrier-hours'),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.ssr ?? options.enabled)
}

interface PopupsQueryData {
  popups: Popup[]
}

export function useActivePopupsQuery(options: StoreQueryOptions = {}) {
  return useServerQuery(useQuery<PopupsQueryData>({
    queryKey: storeKeys.popups,
    queryFn: () => fetchStore<PopupsQueryData>('/api/store/popups'),
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    refetchOnWindowFocus: false,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.ssr ?? options.enabled)
}

export function useProductQuery(
  slug: MaybeRefOrGetter<string>,
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? options.enabled

  return useServerQuery(useQuery<ProductQueryData>({
    queryKey: computed(() => storeKeys.product(toValue(slug))),
    queryFn: () => fetchStore<ProductQueryData>(`/api/store/products/${toValue(slug)}`),
    ...availabilitySensitiveQueryOptions,
    // 404 to odpowiedź („nie ma produktu"), nie awaria — ponowienie dawało drugie
    // wywołanie productdetail i ~1 s opóźnienia każdej strony 404. Reszta błędów
    // jak globalne `retry: 1` (plugins/vue-query.ts).
    retry: (failureCount, error) => {
      const failure = error as { statusCode?: number, status?: number } | null
      return (failure?.statusCode ?? failure?.status) !== 404 && failureCount < 1
    },
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), ssrBlocking)
}

export function useProductQuickViewQuery(slug: MaybeRefOrGetter<string | null>) {
  return useQuery<ProductQueryData>({
    queryKey: computed(() => storeKeys.product(toValue(slug) ?? '')),
    queryFn: () => fetchStore<ProductQueryData>(`/api/store/products/${toValue(slug)}`),
    enabled: computed(() => Boolean(toValue(slug))),
    staleTime: 5 * 60_000,
  })
}

export function useProductRecommendationsQuery(
  productId: MaybeRefOrGetter<string>,
  categoryId: MaybeRefOrGetter<string> = '',
  options: StoreQueryOptions = {},
) {
  return useQuery<ProductRecommendationsData>({
    queryKey: computed(() => ['store', 'product-recommendations', toValue(productId)]),
    queryFn: () => fetchStore<ProductRecommendationsData>('/api/store/products/recommendations', {
      query: { id_product: toValue(productId), id_category: toValue(categoryId) || undefined },
    }),
    enabled: computed(() => Boolean(toValue(productId)) && (toValue(options.enabled) ?? true)),
    ...availabilitySensitiveQueryOptions,
  })
}

export function useCartRecommendationsQuery(
  productIds: MaybeRefOrGetter<string[]>,
  excludeHashes: MaybeRefOrGetter<string[]> = [],
  options: StoreQueryOptions = {},
) {
  const sortedIds = computed(() =>
    [...toValue(productIds)].map(String).filter(Boolean).sort(),
  )
  const sortedHashes = computed(() =>
    [...toValue(excludeHashes)].map(String).map(h => h.trim())
      .filter(h => h.length > 0 && h !== 'not known yet').sort(),
  )
  return useQuery<CartRecommendationsData>({
    queryKey: computed(() => ['store', 'cart-recommendations', sortedIds.value.join(','), sortedHashes.value.join(',')]),
    queryFn: () => fetchStore<CartRecommendationsData>('/api/store/cart/recommendations', {
      query: {
        id_products: sortedIds.value.join(','),
        exclude_hashes: sortedHashes.value.join(',') || undefined,
      },
    }),
    enabled: computed(() => sortedIds.value.length > 0 && (toValue(options.enabled) ?? true)),
    ...availabilitySensitiveQueryOptions,
  })
}

export function useSearchQuery(
  query: MaybeRefOrGetter<string>,
  extraQuery?: MaybeRefOrGetter<Record<string, unknown>>,
) {
  return useServerQuery(useQuery<SearchQueryData>({
    queryKey: computed(() => [...storeKeys.search(toValue(query)), toValue(extraQuery) ?? {}]),
    queryFn: () => fetchStore<SearchQueryData>('/api/store/search', {
      query: {
        ...toValue(extraQuery),
        q: toValue(query),
      },
    }),
    // Zachowaj poprzednie wyniki przy zmianie sortowania/filtra/strony — skeleton
    // (isLoading) pokazuje się tylko przy pierwszym wyszukiwaniu, kolejne
    // interakcje przyciemniają listę (is-fetching), jak na pozostałych listingach.
    placeholderData: previousData => previousData,
    ...availabilitySensitiveQueryOptions,
  }))
}

// Facety wyszukiwarki — osobny request (jak useCategoryFiltersQuery /
// useNewProductsFiltersQuery). Endpoint /api/store/search/filters wysyła
// response_scope=filters, co omija fast-path ElasticJet zwracający puste facety
// dla listing_type=search. Domyślnie nie blokuje SSR (ssr:false).
export function useSearchFiltersQuery(
  phrase: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<Record<string, unknown>> = {},
  options: StoreQueryOptions = {},
) {
  const ssrBlocking = options.ssr ?? false
  return useServerQuery(useQuery<SearchFiltersQueryData>({
    queryKey: computed(() => ['store', 'search-filters', toValue(phrase), ...normalizeListingQueryState(toValue(query) as Record<string, never>)]),
    queryFn: () => fetchStore<SearchFiltersQueryData>('/api/store/search/filters', {
      query: {
        ...(toValue(query) as Record<string, unknown>),
        q: toValue(phrase),
      },
    }),
    placeholderData: options.keepPreviousData === false ? undefined : previousData => previousData,
    ...availabilitySensitiveQueryOptions,
    enabled: computed(() => Boolean(toValue(phrase)) && (toValue(options.enabled) ?? true)),
  }), ssrBlocking)
}

export function useSearchOverlayQuery(query: MaybeRefOrGetter<string>) {
  return useQuery<SearchQueryData>({
    queryKey: computed(() => storeKeys.search(toValue(query))),
    queryFn: () => $fetch<SearchQueryData>('/api/store/search', {
      credentials: 'include',
      query: { q: toValue(query) },
    }),
    enabled: computed(() => toValue(query).length > 1),
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  })
}

export function useCartQuery() {
  // Koszyk jest per-sesja (zależny od cookie PS) — CELOWO NIE używamy useServerQuery.
  // Gdyby leciał na SSR (onServerPrefetch), jego wynik dehydratowałby się do payloadu
  // strony, a strony /products/** i / są SWR-cache'owane (nuxt.config routeRules) —
  // payload pierwszego (sesyjnie pustego) requesta byłby serwowany wszystkim userom,
  // dając pusty badge mimo produktów w koszyku (rozjazd naprawiany dopiero po add).
  // Klient i tak wysyła cookie natywnie, więc fetch działa bez ręcznego forwardu.
  // refetchOnMount: 'always' gwarantuje świeży koszyk przy każdym wejściu/nawigacji.
  const queryClient = useQueryClient()
  return useQuery<CartQueryData>({
    queryKey: storeKeys.cart,
    queryFn: async () => {
      const next = await fetchStore<CartQueryData>('/api/store/cart', { headers: cartIdentityHeaders() })
      // Utrwal token z KAŻDEJ odpowiedzi — to on pozwala odzyskać koszyk, gdy cookie
      // sesji przestanie się dekodować. Puste wartości są ignorowane w setterze.
      setCartIdentityToken(next?.cartToken)
      // Guard „nie kasuj dobrego koszyka": gdy odczyt wrócił pusty WYŁĄCZNIE z
      // powodu chwilowo utraconej sesji (sessionLost), a w cache jest niepusty
      // koszyk — zachowaj poprzedni zamiast nadpisać go pustką. Realnie pusty
      // koszyk (HTTP 200 bez sessionLost) przechodzi normalnie. Patrz
      // resolveCartQueryResult / diagnoza „session cookie present but NOT decodable".
      const previous = queryClient.getQueryData<CartQueryData>(storeKeys.cart)
      const { data, keptPrevious } = resolveCartQueryResult(next, previous)
      if (keptPrevious && previous) {
        // Uczyń problem mierzalnym (dotąd Users affected=0, bo front maskował błąd).
        reportCartSessionLost({ cartId: previous.cartId, itemsCount: previous.itemsCount })
        // Wymuś re-provision świeżej sesji przy następnym dodaniu do koszyka.
        resetGuestCartCache()
      }
      return data
    },
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // W trybie katalogowym koszyka nie ma, a backend odpowiada na niego 403.
    // Bez tego warunku każde wejście na dowolną podstronę strzelałoby po
    // odrzucany endpoint, bo badge koszyka siedzi w nagłówku.
    enabled: computed(() => useRuntimeConfig().public.catalogOnly !== true),
  })
}

export function useCartCouponsQuery() {
  return useQuery<StoreCartCouponsData>({
    queryKey: storeKeys.cartCoupons,
    queryFn: () => fetchStore<StoreCartCouponsData>('/api/store/cart/coupons'),
    staleTime: 30 * 1000,
    enabled: computed(() => useRuntimeConfig().public.catalogOnly !== true),
  })
}

/**
 * Zwraca funkcję inwalidującą zapytania koszyka (pozycje + kupony). Używane do
 * live-sync z aplikacją InPost: po evencie basketowym (InpostPayButton →
 * listenForBasketChanged) backend już zaktualizował \Cart, więc wystarczy wymusić
 * refetch, by zmiany ilości/usunięcia z apki pojawiły się bez reloadu strony.
 */
export function useRefreshCart() {
  const queryClient = useQueryClient()
  return () => invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.cartCoupons])
}

export function useHighlightedPromoCoupon() {
  const { data } = useCartCouponsQuery()
  return computed<StorePromoCoupon | null>(() => {
    const available = data.value?.available ?? []
    return available.find(coupon => coupon.highlight) ?? null
  })
}

export function useBannersQuery() {
  return useServerQuery(useQuery<StoreBannersData>({
    queryKey: storeKeys.banners,
    queryFn: () => fetchStore<StoreBannersData>('/api/store/banners'),
    staleTime: 60 * 1000,
  }))
}

export function useShopReviewsQuery() {
  return useServerQuery(useQuery<ShopReviewsData>({
    queryKey: storeKeys.reviews,
    queryFn: () => fetchStore<ShopReviewsData>('/api/store/reviews'),
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
    refetchOnWindowFocus: false,
  }))
}

export function useActiveBanners(hook?: MaybeRefOrGetter<string | null | undefined>) {
  const { data } = useBannersQuery()
  return computed<StoreBanner[]>(() => {
    const all = data.value?.banners ?? []
    const filterHook = toValue(hook)
    if (!filterHook) return all
    return all.filter(banner => banner.hook === filterHook)
  })
}

export function useWishlistQuery() {
  const authStore = useAuthStore()
  const enabled = computed(() => authStore.isLoggedIn)
  return useQuery<WishlistQueryData>({
    queryKey: storeKeys.wishlist,
    queryFn: () => fetchStore<WishlistQueryData>('/api/store/wishlist'),
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Metody dostawy dla koszyka. `country` = ISO2 kraju adresu dostawy wybranego w formularzu
 * kasy: backend liczy listę dla niego, zanim adres trafi do koszyka (wysyłka zagraniczna —
 * InPost International). Kraj jest w kluczu, więc zmiana kraju to osobna lista, a nie
 * nadpisanie polskiej.
 */
export function useShippingMethodsQuery(
  options: { enabled?: MaybeRefOrGetter<boolean>, country?: MaybeRefOrGetter<string> } = {},
) {
  const country = computed(() => (toValue(options.country) ?? '').trim().toUpperCase())
  return useQuery<ShippingMethod[]>({
    queryKey: computed(() => storeKeys.shippingMethodsFor(country.value)),
    // Kraj z KLUCZA, nie z domknięcia: refetch nieaktywnej listy (np. FR po przejściu na BE)
    // pobrałby inaczej bieżący kraj i zapisał go pod cudzym kluczem.
    queryFn: ({ queryKey }) => {
      const keyCountry = typeof queryKey[3] === 'string' ? queryKey[3] : ''
      return fetchStore<ShippingMethod[]>(
        keyCountry
          ? `/api/store/checkout/carriers?country=${encodeURIComponent(keyCountry)}`
          : '/api/store/checkout/carriers',
      )
    },
    staleTime: 60 * 1000,
    enabled: computed(() => toValue(options.enabled) ?? true),
  })
}

export function usePaymentMethodsQuery() {
  return useQuery<PaymentMethod[]>({
    queryKey: storeKeys.paymentMethods,
    queryFn: () => fetchStore<PaymentMethod[]>('/api/store/checkout/payment-options'),
    staleTime: 60 * 1000,
  })
}

export function useOrdersQuery() {
  return useQuery<Order[]>({
    queryKey: storeKeys.orders,
    queryFn: () => fetchStore<Order[]>('/api/store/orders'),
    staleTime: 60 * 1000,
  })
}

export function useOrderQuery(
  id: MaybeRefOrGetter<string>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useQuery<Order>({
    queryKey: computed(() => storeKeys.order(toValue(id))),
    queryFn: () => fetchStore<Order>(`/api/store/orders/${toValue(id)}`),
    staleTime: 60 * 1000,
    // A 404 (order missing / not the user's) is a definitive answer — retrying it
    // 3× with backoff would only delay the not-found redirect by several seconds.
    retry: false,
    enabled: computed(() => Boolean(toValue(id)) && (toValue(options.enabled) ?? true)),
  })
}

export function useReturnsQuery() {
  return useQuery<ReturnRequest[]>({
    queryKey: storeKeys.returns,
    queryFn: () => fetchStore<ReturnRequest[]>('/api/store/returns'),
    staleTime: 60 * 1000,
  })
}

export function useReturnQuery(id: MaybeRefOrGetter<string>) {
  return useQuery<ReturnRequest>({
    queryKey: computed(() => storeKeys.returnItem(toValue(id))),
    queryFn: () => fetchStore<ReturnRequest>(`/api/store/returns/${toValue(id)}`),
    staleTime: 60 * 1000,
  })
}

export function useAccountInfoQuery() {
  const { sellingEnabled } = useCatalogOnly()

  return useQuery<AccountInfoData>({
    queryKey: storeKeys.accountInfo,
    queryFn: () => fetchStore<AccountInfoData>('/api/store/account/info'),
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    // W trybie katalogowym backend odpowiada 403 na ścieżkach konta, więc
    // pytanie o dane konta jest z definicji nieudane. Bez tego każda podstrona
    // zostawia błąd w konsoli i zaśmieca diagnostykę prawdziwych usterek.
    enabled: sellingEnabled,
  })
}

export function useAddressesQuery() {
  return useQuery<Address[]>({
    queryKey: storeKeys.addresses,
    queryFn: () => fetchStore<Address[]>('/api/store/account/addresses'),
    staleTime: 60 * 1000,
  })
}

/**
 * Statystyki afiliacyjne twórcy za wybrany okres.
 *
 * Klucz zapytania zawiera zakres dat, więc zmiana okresu w panelu to nowe
 * zapytanie, a nie nadpisanie tych samych danych. Celowo bez prefetchu SSR —
 * zapytania konta w tym repo nie są prefetchowane i tego nie zmieniamy.
 */
export function useAffiliateStatsQuery(range: Ref<{ from: string, to: string }>) {
  return useQuery<AffiliateStats>({
    queryKey: computed(() => [...storeKeys.affiliate, range.value.from, range.value.to]),
    queryFn: () => fetchStore<AffiliateStats>(
      `/api/store/account/affiliate?from=${range.value.from}&to=${range.value.to}`,
    ),
    staleTime: 30 * 1000,
  })
}

export function useAddressFormQuery() {
  return useQuery<CountryOption[]>({
    queryKey: storeKeys.addressForm,
    queryFn: () => fetchStore<CountryOption[]>('/api/store/account/address-form'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useSetCheckoutAddressesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_address_delivery?: number; id_address_invoice?: number; id_address?: number }) => fetchStore('/api/store/checkout/address', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart])
    },
  })
}

export function useSetCheckoutCarrierMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_carrier: number; id_address?: number; delivery_message?: string }) => fetchStore<SetCheckoutCarrierResult>('/api/store/checkout/carrier', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.shippingMethods])
    },
  })
}

export function usePrepareCheckoutMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CheckoutPreparePayload) => fetchStore<CheckoutPrepareResult>('/api/store/checkout/prepare', {
      method: 'POST',
      // GET cart can recover an anonymous cart without persisting a new cookie.
      // Prepare must resolve that same cart before creating the guest/addresses;
      // the customer persistence step then writes the cookie used by payment.
      headers: cartIdentityHeaders(),
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.shippingMethods])
    },
  })
}

export function useCheckoutValidateMutation() {
  return useMutation({
    mutationFn: (payload: CheckoutValidatePayload) => fetchStore<CheckoutValidationResult>('/api/store/checkout/validate', {
      method: 'POST',
      body: payload,
    }),
  })
}

export function useCreateOrderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateOrderPayload) => fetchStore<OrderCreateResult>('/api/store/orders/create', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

export interface P24RedirectResult {
  redirectUrl: string
  idCart: number
  idOrder: number
}

export interface P24BlikInitResult {
  sessionHash: string
  pollingIntervalMs: number
}

export interface P24StatusResult {
  status: 'pending' | 'success' | 'error'
  completed: boolean
  message: string | null
  idOrder: number | null
  // secure_key utworzonego zamówienia — pozwala zbudować publiczny link podglądu
  // gościa (/zamowienie/{id}?key=...) bez wymuszania logowania. null gdy brak.
  secureKey: string | null
}

/** POST /api/store/checkout/p24-redirect — inicjuje płatność P24 z przekierowaniem na bramkę. */
export function useP24RedirectMutation() {
  return useMutation({
    mutationFn: (payload: { id_payment: number }) =>
      fetchStore<P24RedirectResult>('/api/store/checkout/p24-redirect', {
        method: 'POST',
        body: payload,
      }),
  })
}

/** POST /api/store/checkout/p24-blik — inicjuje płatność BLIK Zero (6-cyfrowy kod). */
export function useP24BlikMutation() {
  return useMutation({
    mutationFn: (payload: { id_payment: number; blik_code: string }) =>
      fetchStore<P24BlikInitResult>('/api/store/checkout/p24-blik', {
        method: 'POST',
        body: payload,
      }),
  })
}

/**
 * Pobiera bieżący status transakcji P24 (BLIK lub redirect). Niezależne wywołanie
 * — używane przez stronę `/checkout/status` w pętli polling co `pollingIntervalMs`.
 */
export function fetchP24Status(sessionHash: string, options: { verify?: boolean } = {}) {
  return fetchStore<P24StatusResult>('/api/store/checkout/p24-status', {
    // verify=1 → backend przy "pending" aktywnie odpytuje API P24 i potwierdza
    // transakcję (fallback na zgubiony/spóźniony webhook). Front wysyła flagę
    // dopiero na końcu szybkiego okna pollingu — nie przy każdej próbie.
    query: { session_hash: sessionHash, ...(options.verify ? { verify: '1' } : {}) },
  })
}

export interface TpayRedirectResult {
  redirectUrl: string
  idCart: number
  idOrder: number
}

export interface TpayBlikInitResult {
  status: 'pending' | 'error'
  completed: boolean
  message: string | null
  transactionId: string
  pollingIntervalMs: number
  idOrder: number | null
}

export interface TpayStatusResult {
  status: 'pending' | 'success' | 'error'
  completed: boolean
  message: string | null
  idOrder: number | null
  // secure_key utworzonego zamówienia — patrz P24StatusResult.secureKey.
  secureKey: string | null
}

/**
 * POST /api/store/checkout/tpay-redirect — inicjuje płatność Tpay z przekierowaniem
 * na bramkę (przelew / pay-by-link / karta). Tworzy order po stronie PrestaShop —
 * invalidujemy `cart` i `orders` po sukcesie.
 */
export function useTpayRedirectMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { channel_id?: number; group_id?: number } = {}) =>
      fetchStore<TpayRedirectResult>('/api/store/checkout/tpay-redirect', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * POST /api/store/checkout/tpay-blik — inicjuje płatność Tpay BLIK 0 (6-cyfrowy
 * kod). Zwraca `transactionId` używany do pollingu statusu. Tworzy order po
 * stronie PrestaShop — invalidujemy `cart` i `orders` po sukcesie.
 */
export function useTpayBlikMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { blik_code: string }) =>
      fetchStore<TpayBlikInitResult>('/api/store/checkout/tpay-blik', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * Wynik inicjacji płatności portfelem Tpay (Google Pay charge, Apple Pay charge).
 * Kształtem identyczny z BLIK 0 — `transactionId` służy do pollingu statusu.
 */
export type TpayWalletInitResult = TpayBlikInitResult

/** Wynik kroku `validate` Apple Pay — surowa sesja merchanta z Tpay/Apple. */
export interface ApplePayMerchantSession {
  merchantSession: unknown
}

/**
 * POST /api/store/checkout/tpay-googlepay — przekazuje zaszyfrowany token Google Pay
 * do backendu, który inicjuje płatność on-site. Tworzy order po stronie PrestaShop —
 * invalidujemy `cart` i `orders` po sukcesie. `transactionId` służy do pollingu statusu.
 */
export function useTpayGooglePayMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { payload: string }) =>
      fetchStore<TpayWalletInitResult>('/api/store/checkout/tpay-googlepay', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * POST /api/store/checkout/tpay-applepay — dwuetapowy przepływ Apple Pay JS:
 *  - `step:'validate'` → walidacja sesji merchanta (zwraca `{ merchantSession }`),
 *  - `step:'charge'`   → obciążenie tokenem płatności (zwraca `TpayWalletInitResult`).
 * Cache `cart`/`orders` invalidujemy tylko po obciążeniu (charge), bo dopiero ono
 * tworzy order po stronie PrestaShop.
 */
export function useTpayApplePayMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (
      payload:
        | { step: 'validate'; validation_url: string; domain?: string }
        | { step: 'charge'; payload: string },
    ) =>
      fetchStore<TpayWalletInitResult | ApplePayMerchantSession>('/api/store/checkout/tpay-applepay', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async (_data, vars) => {
      if (vars.step === 'charge') {
        await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
      }
    },
  })
}

/**
 * Pobiera bieżący status transakcji Tpay. Używane przez `/checkout/status` w pętli
 * polling — po `transactionId` (BLIK 0) lub po `orderId` (powrót z bramki redirect,
 * gdzie SPA zna tylko id zamówienia).
 */
export function fetchTpayStatus(params: { transactionId?: string; orderId?: number }) {
  const query: Record<string, string | number> = {}
  if (params.transactionId) query.transaction_id = params.transactionId
  else if (params.orderId) query.order_id = params.orderId
  return fetchStore<TpayStatusResult>('/api/store/checkout/tpay-status', { query })
}

/**
 * GET /api/store/checkout/bluepayment-gateways — lista dostępnych bramek BluePayment / Autopay.
 *
 * Cache 5 min (`staleTime`) — lista bramek rzadko się zmienia, ale konfiguracja
 * sklepu w PrestaShop może być modyfikowana, więc nie cachujemy agresywniej.
 * Opcjonalny `enabled` gate — zwykle odpalane dopiero gdy `kind` metody
 * płatności w `CheckoutPayment.vue` jest jednym z `bluepayment-*`.
 */
export function useBluepaymentGatewaysQuery(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useServerQuery(useQuery<BluepaymentGatewayList>({
    queryKey: storeKeys.bluepaymentGateways,
    queryFn: () => fetchStore<BluepaymentGatewayList>('/api/store/checkout/bluepayment-gateways'),
    staleTime: 5 * 60_000,
    enabled: computed(() => toValue(options.enabled) ?? true),
  }), options.enabled)
}

/**
 * POST /api/store/checkout/bluepayment-payment — inicjuje standardową płatność
 * BluePayment / Autopay (redirect form auto-submit). Zwraca `redirectForm` (HTML).
 * Tworzy order po stronie PrestaShop — invalidujemy `cart` i `orders` po sukcesie.
 */
export function useBluepaymentPaymentMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vars: { gatewayId: number; regulationId?: string }) =>
      fetchStore<BluepaymentPaymentResult>('/api/store/checkout/bluepayment-payment', {
        method: 'POST',
        body: { gateway_id: vars.gatewayId, regulation_id: vars.regulationId ?? '' },
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * POST /api/store/checkout/bluepayment-blik — inicjuje płatność BLIK 0 (6-cyfrowy kod).
 * Zwraca `postOrderId` używany do pollingu statusu. Tworzy order po stronie
 * PrestaShop — invalidujemy `cart` i `orders` po sukcesie.
 */
export function useBluepaymentBlikMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vars: { blikCode: string }) =>
      fetchStore<BluepaymentPollingResult>('/api/store/checkout/bluepayment-blik', {
        method: 'POST',
        body: { blik_code: vars.blikCode },
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * POST /api/store/checkout/ps-cashondelivery — wpina natywny modul PS Cash on
 * Delivery. Body puste; backend bierze cart/adres z sesji. Po sukcesie:
 * order utworzony po stronie PrestaShop (cart skonsumowany) — invalidujemy
 * `cart` i `orders`.
 */
export function usePsCashOnDeliveryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      fetchStore<CodOrderResult>('/api/store/checkout/ps-cashondelivery', {
        method: 'POST',
        body: {},
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.orders])
    },
  })
}

/**
 * Pobiera bieżący status transakcji BluePayment BLIK. Niezależne wywołanie —
 * używane przez stronę `/checkout/status` w pętli polling po `postOrderId`.
 */
export function fetchBluepaymentBlikStatus(postOrderId: string) {
  return fetchStore<BluepaymentPollingResult>('/api/store/checkout/bluepayment-blik-status', {
    query: { post_order_id: postOrderId },
  })
}

export function useAddToCartMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { productId: number; productAttributeId?: number; quantity?: number }) => fetchStore<CartQueryData>('/api/store/cart', {
      method: 'POST',
      headers: cartIdentityHeaders(),
      body: {
        action: 'add',
        productId: payload.productId,
        productAttributeId: payload.productAttributeId ?? 0,
        quantity: payload.quantity ?? 1,
      },
    }),
    onSuccess: async (data) => {
      await adoptCartMutationResult(queryClient, data)
    },
  })
}

export function useUpdateCartLineMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { lineId: string; quantity: number; op?: 'up' | 'down'; targetQuantity?: number }) => fetchStore('/api/store/cart', {
      method: 'POST',
      headers: cartIdentityHeaders(),
      body: {
        action: 'update',
        lineId: payload.lineId,
        quantity: payload.quantity,
        op: payload.op,
      },
    }),
    onSuccess: async (data) => {
      await adoptCartMutationResult(queryClient, data)
    },
  })
}

export function useRemoveCartLineMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { lineId: string }) => fetchStore('/api/store/cart', {
      method: 'POST',
      headers: cartIdentityHeaders(),
      body: {
        action: 'delete',
        ...payload,
      },
    }),
    onSuccess: async (data) => {
      await adoptCartMutationResult(queryClient, data)
    },
  })
}

/**
 * Odrzucenie wyprzedanej pozycji. Odpowiedź NIE jest koszykiem (pozycja nie leży
 * w `cart_product`), więc nie da się jej wstawić do cache jak zwykłej mutacji —
 * unieważniamy zapytanie, żeby sekcja „Wyprzedane" przeładowała się z backendu.
 */
export function useDismissUnavailableCartLineMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { lineId: string }) => fetchStore('/api/store/cart/unavailable', {
      method: 'DELETE',
      headers: cartIdentityHeaders(),
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart])
    },
  })
}

/**
 * Przeniesienie pozycji z sekcji „Wyprzedane" z powrotem do koszyka. Zmienia skład
 * koszyka i jego sumy, więc koszyk musi zostać przeładowany.
 */
export function useRestoreUnavailableCartLineMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { lineId: string }) => fetchStore('/api/store/cart/unavailable', {
      method: 'POST',
      headers: cartIdentityHeaders(),
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.cartCoupons])
    },
  })
}

/**
 * Odzyskanie porzuconego koszyka z linku w mailu SALESmanago (/cart-recover).
 * Odpowiedź NIE jest koszykiem, tylko bilansem przeniesionych pozycji, więc
 * koszyk i kupony przeładowujemy z backendu — jak przy przywróceniu wyprzedanej
 * pozycji. Token tożsamości utrwalamy od razu: link z maila często otwiera się
 * w świeżej przeglądarce, w której backend dopiero co utworzył koszyk.
 */
export function useRecoverCartMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { cart: number; token: string }) => fetchStore<CartRecoverResult>('/api/store/cart/recover', {
      method: 'POST',
      headers: cartIdentityHeaders(),
      body: payload,
    }),
    onSuccess: async (data) => {
      setCartIdentityToken(data?.cartToken)
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.cartCoupons])
    },
  })
}

export function useApplyCouponMutation() {
  const queryClient = useQueryClient()
  const notifications = useNotifications()

  return useMutation({
    mutationFn: (code: string) => fetchStore('/api/store/cart/coupons', {
      method: 'POST',
      body: { code },
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart])
      notifications.success('Kod rabatowy został zastosowany.')
    },
    onError: (error: unknown) => {
      notifications.error(extractApiErrorMessage(error, 'Nie udało się zastosować kodu rabatowego.'))
    },
  })
}

export function useToggleWishlistMutation() {
  const queryClient = useQueryClient()
  const notifications = useNotifications()
  const authStore = useAuthStore()

  return useMutation({
    mutationFn: ({ productId, isFavorite }: { productId: string; isFavorite: boolean }) => fetchStore('/api/store/wishlist', {
      method: isFavorite ? 'DELETE' : 'POST',
      body: { productId },
    }),
    // Optimistic update — serce na karcie/PDP i lista ulubionych reagują natychmiast,
    // bez czekania na (wolny) round-trip do PrestaShop.
    onMutate: async ({ productId, isFavorite }) => {
      // Gość: nie ruszamy cache ani nie pokazujemy toasta sukcesu — request i tak
      // zwróci 401, który obsłuży onError (uniknięcie błysku sukces→błąd).
      if (!authStore.isLoggedIn) {
        return { previous: undefined as WishlistQueryData | undefined }
      }

      await queryClient.cancelQueries({ queryKey: storeKeys.wishlist })
      const previous = queryClient.getQueryData<WishlistQueryData>(storeKeys.wishlist)

      queryClient.setQueryData<WishlistQueryData>(storeKeys.wishlist, (old) => {
        const ids = new Set(old?.productIds ?? [])
        const products = old?.products ?? []
        if (isFavorite) {
          ids.delete(productId)
          return { productIds: [...ids], products: products.filter(p => p.id !== productId) }
        }
        ids.add(productId)
        return { productIds: [...ids], products }
      })

      notifications.success(isFavorite ? 'Usunięto z ulubionych.' : 'Dodano do ulubionych.')
      return { previous }
    },
    onError: (error: unknown, _vars, context) => {
      // Cofnij optymistyczną zmianę do stanu sprzed kliknięcia (gdy istniał snapshot).
      if (context?.previous !== undefined) {
        queryClient.setQueryData(storeKeys.wishlist, context.previous)
      }
      const code = (error as { data?: { statusCode?: number }; status?: number })?.data?.statusCode
        ?? (error as { status?: number })?.status
      if (code === 401) {
        notifications.error('Musisz być zalogowany, aby korzystać z listy ulubionych.')
      } else {
        notifications.error('Nie udało się zaktualizować listy ulubionych.')
      }
    },
    // Reconcile w tle — niewidoczny dla usera, bo UI już przeskoczyło w onMutate.
    // Świadomie invalidate (a NIE setQueryData(odpowiedź)): przy szybkim przełączaniu
    // dodaj→usuń odpowiedzi mogą wrócić w odwrotnej kolejności i zapisałyby nieaktualny
    // stan; invalidate pyta serwer „co jest teraz prawdą" po ustabilizowaniu.
    onSettled: () => {
      return invalidateStoreQueries(queryClient, [storeKeys.wishlist])
    },
  })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { email: string; password: string }) => fetchStore('/api/store/auth/login', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.accountInfo, storeKeys.cart, storeKeys.wishlist, storeKeys.orders])
    },
  })
}

export function useUpdateAccountMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AccountUpdatePayload) => fetchStore('/api/store/account/edit', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.accountInfo])
    },
  })
}

export function useCreateAddressMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AddressPayload) => fetchStore('/api/store/account/addresses', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.addresses, storeKeys.cart])
    },
  })
}

export function useUpdateAddressMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id: string; address: AddressPayload }) => fetchStore(`/api/store/account/addresses/${payload.id}`, {
      method: 'PUT',
      body: payload.address,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.addresses, storeKeys.cart])
    },
  })
}

export function useDeleteAddressMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => fetchStore(`/api/store/account/addresses/${id}`, {
      method: 'DELETE',
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.addresses, storeKeys.cart])
    },
  })
}

export function useSetDefaultAddressMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => fetchStore<{ updated: boolean; idDefaultAddress: number }>(
      '/api/store/account/addresses/set-default',
      {
        method: 'POST',
        body: { id_address: Number(id) },
      },
    ),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.addresses, storeKeys.accountInfo])
    },
  })
}

export function useCancelOrderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (idOrder: string | number) => fetchStore<{
      cancelled: boolean
      id_order: number
      current_state: number
      state_label: string
    }>(`/api/store/orders/${idOrder}/cancel`, {
      method: 'POST',
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.orders])
    },
  })
}

export interface RepayResult {
  redirectUrl: string
  paymentMethod: string
}

/** POST /api/store/orders/:id/repay — generuje link do ponowienia płatności. */
export function useRepayOrderMutation() {
  return useMutation({
    mutationFn: (idOrder: string | number) =>
      fetchStore<RepayResult>(`/api/store/orders/${idOrder}/repay`, { method: 'POST' }),
  })
}

export function useDeleteAccountMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => fetchStore<{ deleted: boolean; message: string }>('/api/store/account', {
      method: 'DELETE',
    }),
    onSuccess: async () => {
      // Drop every cached query – the account is gone and a public visitor
      // should not see addresses, orders, cart, wishlist, etc.
      queryClient.removeQueries()
    },
  })
}

export function useRegisterMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { email: string; firstName: string; lastName: string; password: string; newsletter: boolean }) => fetchStore('/api/store/auth/register', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.accountInfo, storeKeys.cart, storeKeys.wishlist, storeKeys.orders])
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => fetchStore('/api/store/auth/logout', {
      method: 'POST',
    }),
    onSuccess: async () => {
      await queryClient.resetQueries()
    },
  })
}

export function useSocialPendingQuery() {
  return useQuery<SocialPendingAuth>({
    queryKey: storeKeys.socialPending,
    queryFn: () => fetchStore<SocialPendingAuth>('/api/store/auth/social/pending'),
    enabled: import.meta.client === true,
    retry: false,
    gcTime: 0,
    staleTime: 0,
  })
}

export function useSocialConsentMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { termsAccepted: boolean; privacyAccepted: boolean }) => fetchStore('/api/store/auth/social/consent', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await clearSocialAuthCaches(queryClient)
    },
  })
}

export function usePasswordResetRequestMutation() {
  return useMutation({
    mutationFn: (email: string) => fetchStore('/api/store/auth/password-reset-request', {
      method: 'POST',
      body: { email },
    }),
  })
}

export function usePasswordResetCheckMutation() {
  return useMutation({
    mutationFn: (payload: { email: string; token: string }) => fetchStore('/api/store/auth/password-reset-check', {
      method: 'POST',
      body: payload,
    }),
  })
}

export function usePasswordResetEnterMutation() {
  return useMutation({
    mutationFn: (payload: { email: string; token: string; password: string; password_confirm: string }) => fetchStore('/api/store/auth/password-reset-enter', {
      method: 'POST',
      body: payload,
    }),
  })
}

export function useRemoveCouponMutation() {
  const queryClient = useQueryClient()
  const notifications = useNotifications()

  return useMutation({
    mutationFn: (payload: { code?: string; id_cart_rule?: number }) => fetchStore('/api/store/cart/coupons', {
      method: 'DELETE',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart])
      notifications.success('Kod rabatowy został usunięty.')
    },
    onError: () => {
      notifications.error('Nie udało się usunąć kodu rabatowego.')
    },
  })
}

export function useMergeCartMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => fetchStore('/api/store/cart/merge', {
      method: 'POST',
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart, storeKeys.wishlist])
    },
  })
}

export function useCreateReturnMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_order: number; order_detail_list: { id_order_detail: number; quantity: number }[]; question: string }) => fetchStore('/api/store/returns/create', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.returns])
    },
  })
}

export function useReorderQuery(idOrder: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: computed(() => key('store', 'orders', toValue(idOrder), 'reorder-preview')),
    queryFn: () => fetchStore(`/api/store/orders/reorder`, {
      query: { id_order: toValue(idOrder) },
    }),
    enabled: computed(() => Boolean(toValue(idOrder))),
  })
}

export function useReorderMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (idOrder: number) => fetchStore<CartQueryData>('/api/store/orders/reorder', {
      method: 'POST',
      body: { id_order: idOrder },
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.cart])
    },
  })
}

export function useProductRelatedQuery(idProduct: MaybeRefOrGetter<string>) {
  return useQuery<Product[]>({
    queryKey: computed(() => storeKeys.productRelated(toValue(idProduct))),
    queryFn: () => fetchStore<Product[]>('/api/store/products/related', {
      query: { id_product: toValue(idProduct) },
    }),
    staleTime: 5 * 60 * 1000,
    enabled: computed(() => Boolean(toValue(idProduct))),
  })
}

export function useProductReviewsQuery(idProduct: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: computed(() => storeKeys.productReviews(toValue(idProduct))),
    queryFn: () => fetchStore(`/api/store/products/reviews`, {
      query: { id_product: toValue(idProduct) },
    }),
    staleTime: 2 * 60 * 1000,
    enabled: computed(() => Boolean(toValue(idProduct))),
  })
}

export function usePostReviewMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_product: number; rating: number; title?: string; content: string }) => fetchStore('/api/store/products/reviews', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async (_data, variables) => {
      await invalidateStoreQueries(queryClient, [storeKeys.productReviews(String(variables.id_product))])
    },
  })
}

export function useStockAlertsQuery() {
  return useQuery({
    queryKey: storeKeys.stockAlerts,
    queryFn: () => fetchStore('/api/store/products/stock-alerts'),
    staleTime: 60 * 1000,
  })
}

export function useSubscribeStockAlertMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_product: number; id_product_attribute?: number; email?: string }) => fetchStore('/api/store/products/stock-alerts', {
      method: 'POST',
      body: payload,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.stockAlerts])
    },
  })
}

export function useUnsubscribeStockAlertMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id_product: number; id_product_attribute?: number }) => fetchStore('/api/store/products/stock-alerts', {
      method: 'DELETE',
      query: payload as Record<string, unknown>,
    }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [storeKeys.stockAlerts])
    },
  })
}

export function useNewsletterSubscribeMutation() {
  return useMutation({
    mutationFn: (payload: { email: string }) => fetchStore<{ subscribed: boolean; email: string; message: string }>('/api/store/newsletter/subscribe', {
      method: 'POST',
      body: payload,
    }),
  })
}

export function useGuestSessionQuery() {
  const { sellingEnabled } = useCatalogOnly()

  return useQuery<GuestSessionContract | null>({
    queryKey: storeKeys.guestSession,
    queryFn: () => fetchStore('/api/store/guest-session'),
    staleTime: 30 * 1000,
    // Sesja gościa istnieje wyłącznie po to, żeby dołożyć produkt do koszyka.
    // useAddToCartFlow woła to zapytanie w setup karty produktu, czyli NIEZALEŻNIE
    // od tego, czy przycisk zakupu się renderuje — w trybie katalogowym dawało to
    // dwa żądania z 403 na każdej karcie.
    enabled: sellingEnabled,
  })
}

export function useCreateGuestSessionMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload?: { id_lang?: number; id_currency?: number; id_shop?: number }) =>
      fetchStore('/api/store/guest-session', {
        method: 'POST',
        body: payload ?? {},
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(storeKeys.guestSession, data)
      await invalidateStoreQueries(queryClient, [storeKeys.guestSession])
    },
  })
}

export function useProductStockQuery(
  idProduct: MaybeRefOrGetter<string>,
  idProductAttribute?: MaybeRefOrGetter<string | undefined>,
) {
  return useQuery({
    queryKey: computed(() =>
      storeKeys.productStock(toValue(idProduct), toValue(idProductAttribute ?? undefined)),
    ),
    queryFn: () => {
      const attrId = toValue(idProductAttribute ?? undefined)
      return fetchStore('/api/store/products/stock', {
        query: {
          id_product: toValue(idProduct),
          ...(attrId ? { id_product_attribute: attrId } : {}),
        },
      })
    },
    staleTime: 60 * 1000,
    enabled: computed(() => Boolean(toValue(idProduct))),
  })
}

export function useInpostGeowidgetTokenQuery(enabled?: MaybeRefOrGetter<boolean>) {
  return useQuery<InpostGeowidgetToken>({
    queryKey: storeKeys.inpostGeowidget,
    queryFn: () => fetchStore<InpostGeowidgetToken>('/api/store/checkout/pickup/geowidget-token?provider=inpost'),
    staleTime: 30 * 60 * 1000,
    enabled: computed(() => toValue(enabled) ?? true),
    retry: false,
  })
}

export function useInpostClosestPointQuery(
  postcode: MaybeRefOrGetter<string>,
  carrierId: MaybeRefOrGetter<number>,
) {
  return useQuery<PickupPoint | null>({
    queryKey: computed(() => storeKeys.inpostClosestPoint(toValue(postcode), toValue(carrierId))),
    queryFn: () => fetchStore<PickupPoint | null>(
      `/api/store/checkout/pickup/closest-point?provider=inpost&postcode=${encodeURIComponent(toValue(postcode))}&carrier_id=${toValue(carrierId)}`,
    ),
    enabled: computed(() => Boolean(toValue(postcode)) && toValue(carrierId) > 0),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useInpostCartChoiceQuery() {
  return useQuery<InpostCartChoice>({
    queryKey: storeKeys.inpostCartChoice,
    queryFn: () => fetchStore<InpostCartChoice>('/api/store/checkout/pickup/cart-choice?provider=inpost'),
    staleTime: 10 * 1000,
    retry: false,
  })
}

export function useInpostCartChoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { carrier_id: number; point: string; phone?: string; email?: string }) =>
      fetchStore<InpostCartChoice>('/api/store/checkout/pickup/cart-choice?provider=inpost', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [
        storeKeys.inpostCartChoice,
        storeKeys.shippingMethods,
        storeKeys.cart,
      ])
    },
  })
}

// Czyści wybór usługi/punktu InPost dla koszyka (DELETE). Front woła to przy zmianie
// dostawy na przewoźnika nie-lockerowego, żeby „nieświeży" wybór Paczkomatu nie wisiał
// na koszyku (rozjazd carrier↔wybór InPost → fałszywa blokada COD / paczkomat bez punktu).
export function useInpostCartChoiceClearMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchStore('/api/store/checkout/pickup/cart-choice?provider=inpost', { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [
        storeKeys.inpostCartChoice,
        storeKeys.shippingMethods,
        storeKeys.cart,
      ])
    },
  })
}

export function useOrlenGeowidgetTokenQuery(enabled?: MaybeRefOrGetter<boolean>) {
  return useQuery<OrlenGeowidgetToken>({
    queryKey: storeKeys.orlenGeowidget,
    queryFn: () => fetchStore<OrlenGeowidgetToken>(
      '/api/store/checkout/pickup/geowidget-token?provider=orlen',
    ),
    staleTime: 30 * 60 * 1000,
    enabled: computed(() => toValue(enabled) ?? true),
    retry: false,
  })
}

export function useInpostIntlGeowidgetConfigQuery(enabled?: MaybeRefOrGetter<boolean>) {
  return useQuery<InpostIntlGeowidgetConfig>({
    queryKey: storeKeys.inpostIntlGeowidget,
    queryFn: () => fetchStore<InpostIntlGeowidgetConfig>(
      '/api/store/checkout/pickup/geowidget-token?provider=inpost_intl',
    ),
    staleTime: 30 * 60 * 1000,
    enabled: computed(() => toValue(enabled) ?? true),
    retry: false,
  })
}

/**
 * Zapis punktu InPost International dla koszyka. `country` = kraj adresu dostawy z formularza —
 * backend odrzuci punkt z innego kraju (moduł sam tego przy nadaniu nie sprawdza).
 */
export function useInpostIntlCartChoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { carrier_id: number, point: string, country?: string }) =>
      fetchStore<{ carrier_id: number, point: string, label: string | null }>(
        '/api/store/checkout/pickup/cart-choice?provider=inpost_intl',
        { method: 'POST', body: payload, headers: cartIdentityHeaders() },
      ),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [
        storeKeys.shippingMethods,
        storeKeys.cart,
      ])
    },
  })
}

/**
 * Kraj odwiedzającego + czy do niego wysyłamy (baner „Dostarczamy do Twojego kraju").
 * Tylko po stronie klienta: HTML sklepu jest cache'owany na Cloudflare, więc kraj
 * wyrenderowany w SSR trafiłby do WSZYSTKICH odwiedzających tę stronę.
 */
export function useDeliveryCountryNoticeQuery(
  enabled?: MaybeRefOrGetter<boolean>,
  /** ISO2 do podglądu banera bez VPN (`?delivery_country_preview=FR` na stronie). */
  preview?: MaybeRefOrGetter<string>,
) {
  const previewIso = computed(() => {
    const iso = (toValue(preview) ?? '').trim().toUpperCase()
    return /^[A-Z]{2}$/.test(iso) ? iso : ''
  })
  return useQuery<DeliveryCountryNotice>({
    queryKey: computed(() => previewIso.value
      ? [...storeKeys.deliveryCountryNotice, previewIso.value]
      : storeKeys.deliveryCountryNotice),
    // Pod /api/store/checkout/** celowo: ta ścieżka ma już `no-store, private` i obejście
    // cache na Cloudflare — kraj odwiedzającego nie może trafić do cache współdzielonego.
    queryFn: () => fetchStore<DeliveryCountryNotice>(
      previewIso.value
        ? `/api/store/checkout/delivery-country?preview=${previewIso.value}`
        : '/api/store/checkout/delivery-country',
    ),
    staleTime: 60 * 60 * 1000,
    enabled: computed(() => import.meta.client && (toValue(enabled) ?? true)),
    retry: false,
  })
}

interface OrlenCartChoiceMutationPayload {
  carrier_id: number
  id: string
  name: string
  address: string
  city: string
  postalCode: string
  province: string
  latitude: number
  longitude: number
  destinationCode: string
}

export function useOrlenCartChoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrlenCartChoiceMutationPayload) =>
      fetchStore<unknown>('/api/store/checkout/pickup/cart-choice?provider=orlen', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async () => {
      await invalidateStoreQueries(queryClient, [
        storeKeys.shippingMethods,
        storeKeys.cart,
      ])
    },
  })
}
